"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  LayoutDashboard,
  ScanLine,
  Building2,
  FileText,
  Settings,
  HelpCircle,
  CreditCard,
  GitCompareArrows,
  BookOpen,
  Calculator,
} from "lucide-react";
import { LanguageSelector } from "@/components/nav/language-selector";
import type { Locale } from "@/i18n/config";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export function AppSidebar() {
  const pathname = usePathname();
  const locale = useLocale() as Locale;
  const t = useTranslations("nav");
  const tc = useTranslations("common");

  const mainNavItems = [
    {
      title: t("dashboard"),
      href: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: t("scanContract"),
      href: "/scan/new",
      icon: ScanLine,
    },
    {
      title: t("checkProperty"),
      href: "/property/new",
      icon: Building2,
    },
    {
      title: t("compareDocuments"),
      href: "/scan/compare",
      icon: GitCompareArrows,
    },
    {
      title: t("templates"),
      href: "/templates",
      icon: BookOpen,
    },
    {
      title: t("calculators"),
      href: "/tools",
      icon: Calculator,
    },
  ];

  const secondaryNavItems = [
    {
      title: t("myDocuments"),
      href: "/documents",
      icon: FileText,
    },
    {
      title: t("billing"),
      href: "/billing",
      icon: CreditCard,
    },
    {
      title: t("settings"),
      href: "/settings",
      icon: Settings,
    },
    {
      title: t("help"),
      href: "/help",
      icon: HelpCircle,
    },
  ];

  return (
    <Sidebar className="border-r border-border">
      <SidebarHeader className="p-4">
        <Link href="/" className="flex flex-col gap-0.5">
          <span className="text-2xl uppercase tracking-wide leading-none" style={{ fontFamily: 'var(--font-logo)' }}>
            Got<span className="text-brand">RedFlags</span>
          </span>
          <p className="text-[10px] text-muted-foreground leading-none">
            {tc("tagline")}
          </p>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-muted-foreground text-[10px] uppercase tracking-wider">
            {t("scan")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    render={<Link href={item.href} />}
                    isActive={
                      pathname === item.href ||
                      pathname.startsWith(item.href + "/")
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-muted-foreground text-[10px] uppercase tracking-wider">
            {t("account")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    render={<Link href={item.href} />}
                    isActive={pathname.startsWith(item.href)}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 space-y-3">
        <LanguageSelector currentLocale={locale} compact />
        <div className="rounded-lg bg-surface border border-border p-3">
          <p className="text-[10px] text-muted-foreground mb-2">{t("freePlan")}</p>
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground/80">{t("contractScans")}</span>
            <span className="text-brand font-medium">{t("remaining", { count: 2 })}</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-foreground/80">{t("propertyChecks")}</span>
            <span className="text-warning font-medium">{t("remaining", { count: 1 })}</span>
          </div>
          <Link
            href="/pricing"
            className="mt-2 block w-full rounded-md bg-brand hover:bg-brand/90 py-1.5 text-center text-xs font-medium text-brand-foreground transition-colors"
          >
            {tc("upgrade")}
          </Link>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
